// ---------------------------------------------------------------------------
// App Ontology — structured graph of datasources, flows, state, and UI bindings.
//
// Pure function that transforms raw block/flow data into a directed graph
// powering the Data Flow visualization, cross-tab selection sync, and
// future WebMCP/Chat Agent integration.
// ---------------------------------------------------------------------------

import { walkFlowSteps, extractExpressionRefs } from './flow-analysis'
import type { CrossReferenceIndex } from '@/lib/cross-reference'
import type { EntityIndex } from '@/features/data-explorer/hooks/use-entity-index'
import type { FlowStep, Flow } from '@cogo/types-v2'
import type { Narrative } from '@cogo/types'
import { RUNTIME_REFS } from '@/lib/action-constants'

// ---------------------------------------------------------------------------
// Node types
// ---------------------------------------------------------------------------

export interface OntologyDatasource {
  id: string           // "ds:{blockName}:{flowName}:{index}"
  type: 'datasource'
  blockName: string
  flowName: string
  url: string
  method: string
  resultTarget: string | null
}

export interface OntologyFlowNode {
  id: string           // "flow:{blockName}:{flowName}"
  type: 'flow'
  blockName: string
  flowName: string
  trigger: string      // "init" | "onTap" | etc.
  actionSummary: string // "restApi -> setState -> showToast"
  narrative: Narrative | null  // null = no narrative available (fallback deterministic template)
}

export interface OntologyStateNode {
  id: string           // "state:{blockName}:{key}" or "state:appData:{key}"
  type: 'state'
  key: string
  blockName: string    // "appData" for global
  scope: 'local' | 'appData'
  readByFlows: string[]  // flow node IDs
  writtenByFlows: string[] // flow node IDs
}

export interface OntologyUIBinding {
  id: string           // "ui:{blockName}:{elementId}:{varKey}"
  type: 'uiBinding'
  blockName: string
  elementId: string
  boundVariable: string // state key referenced via #{var}
}

export type OntologyNode = OntologyDatasource | OntologyFlowNode | OntologyStateNode | OntologyUIBinding

export interface OntologyEdge {
  id: string
  source: string       // node ID
  target: string       // node ID
  type: 'fetch' | 'write' | 'read' | 'bind' | 'callFlow' | 'navigate'
  label?: string
}

export interface AppOntology {
  datasources: OntologyDatasource[]
  flows: OntologyFlowNode[]
  states: OntologyStateNode[]
  uiBindings: OntologyUIBinding[]
  edges: OntologyEdge[]
}

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

interface BlockInput {
  flow?: Record<string, Flow>
  ui?: { root?: UiNode }
  state?: Record<string, unknown>
  events?: Record<string, unknown>
  meta?: Record<string, unknown>
  data?: Record<string, unknown>
}

interface UiNode {
  id: string
  type: string
  children?: UiNode[]
  [key: string]: unknown
}

export interface BuildAppOntologyInput {
  blocks: Record<string, BlockInput>
  crossRef?: CrossReferenceIndex
  entityIndex?: EntityIndex
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract UI binding variable references from #{var} expressions.
 * Unlike extractExpressionRefs (which returns only the root name),
 * this preserves `appData.X` two-level paths so cross-block bindings
 * can be matched to state nodes.
 */
function extractUiBindingRefs(text: string): string[] {
  if (!text) return []
  const seen = new Set<string>()
  const results: string[] = []
  const regex = /#\{([^}]+)\}/g
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    const inner = match[1].trim()
    const parts = inner.split(/[.\s+\-*/()[\],?:!<>=&|]/)
    const root = parts[0]
    if (!root || RUNTIME_REFS.has(root) || /^\d/.test(root)) continue

    // For appData refs, preserve the two-level path: appData.X
    let ref = root
    if (root === 'appData' && parts.length > 1 && parts[1]) {
      ref = `appData.${parts[1]}`
    }

    if (!seen.has(ref)) {
      seen.add(ref)
      results.push(ref)
    }
  }

  return results
}

/** Recursively walk all UI elements in a tree. */
function walkUiElements(node: UiNode, fn: (el: UiNode) => void): void {
  fn(node)
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      walkUiElements(child, fn)
    }
  }
}

/** Build a summary string of actions in a flow. */
function buildActionSummary(steps: FlowStep[]): string {
  const actions: string[] = []
  const seen = new Set<string>()

  walkFlowSteps(steps, (step) => {
    if ((step.type === 'basic' || step.type === 'expression') && !seen.has(step.action)) {
      seen.add(step.action)
      actions.push(step.action)
    }
  })

  if (actions.length === 0) return 'empty'
  return actions.slice(0, 4).join(' -> ') + (actions.length > 4 ? ' ...' : '')
}

/** Determine trigger for a flow from crossRef or flow name. */
function determineTrigger(
  flowName: string,
  flow: Flow,
  crossRef: CrossReferenceIndex,
): string {
  // Check flow's own trigger field
  if (flow.trigger === 'init') return 'init'

  // Check flowUsageIndex for event type
  const usages = crossRef.flowUsageIndex.get(flowName)
  if (usages && usages.length > 0) {
    return usages[0].eventType
  }

  // Fallback: init if name starts with "init"
  if (flowName.toLowerCase().startsWith('init')) return 'init'

  return flow.trigger || 'event'
}

/** Check if a state key is appData scope. */
function isAppDataScope(
  key: string,
  entityIndex: EntityIndex,
): boolean {
  if (key.startsWith('appData.')) return true

  // Check entityIndex for variables with scope 'appData'
  return entityIndex.variables.some(
    (v) => v.name === key && v.scope === 'appData',
  )
}

/** Get a state node ID based on scope. */
function stateNodeId(key: string, blockName: string, isAppData: boolean): string {
  if (isAppData) {
    return `state:appData:${key}`
  }
  return `state:${blockName}:${key}`
}

// ---------------------------------------------------------------------------
// buildAppOntology
// ---------------------------------------------------------------------------

const EMPTY_CROSS_REF: CrossReferenceIndex = {
  flowUsageIndex: new Map(),
  stateUsageIndex: new Map(),
  navigationIndex: new Map(),
  elementEventIndex: new Map(),
} as CrossReferenceIndex

const EMPTY_ENTITY_INDEX = {
  variables: [],
  datasources: [],
  flows: [],
  events: [],
} as unknown as EntityIndex

export function buildAppOntology(input: BuildAppOntologyInput): AppOntology {
  const { blocks, crossRef = EMPTY_CROSS_REF, entityIndex = EMPTY_ENTITY_INDEX } = input

  const datasources: OntologyDatasource[] = []
  const flows: OntologyFlowNode[] = []
  const stateMap = new Map<string, OntologyStateNode>()
  const uiBindings: OntologyUIBinding[] = []
  const edges: OntologyEdge[] = []
  let edgeCounter = 0

  const nextEdgeId = () => `edge:${edgeCounter++}`

  // -------------------------------------------------------------------------
  // 1. Process each block's flows: extract datasources, flow nodes, callFlow edges
  // -------------------------------------------------------------------------

  for (const [blockName, block] of Object.entries(blocks)) {
    const blockFlows = block.flow ?? {}

    for (const [flowName, flow] of Object.entries(blockFlows)) {
      if (!flow?.steps) continue

      const flowNodeId = `flow:${blockName}:${flowName}`

      // Create flow node
      const flowNarrative = (flow as { narrative?: Narrative }).narrative ?? null
      flows.push({
        id: flowNodeId,
        type: 'flow',
        blockName,
        flowName,
        trigger: determineTrigger(flowName, flow, crossRef),
        actionSummary: buildActionSummary(flow.steps),
        narrative: flowNarrative,
      })

      // Walk steps for restApi datasources and callFlow edges
      let dsIndex = 0
      walkFlowSteps(flow.steps, (step) => {
        if (step.type !== 'basic' && step.type !== 'expression') return

        // restApi datasource extraction
        if (step.action === 'restApi') {
          const params = step.params ?? {}
          const dsId = `ds:${blockName}:${flowName}:${dsIndex++}`

          datasources.push({
            id: dsId,
            type: 'datasource',
            blockName,
            flowName,
            url: String(params.url ?? ''),
            method: String(params.method ?? 'GET'),
            resultTarget: params.resultTarget ? String(params.resultTarget) : null,
          })

          // datasource -> flow (fetch edge)
          edges.push({
            id: nextEdgeId(),
            source: dsId,
            target: flowNodeId,
            type: 'fetch',
          })
        }

        // callFlow edge
        if (step.action === 'callFlow' && step.params?.flowName) {
          const targetFlowName = String(step.params.flowName)
          // Try to find target flow in same block first, then other blocks
          let targetFlowNodeId: string | null = null

          if (blockFlows[targetFlowName]) {
            targetFlowNodeId = `flow:${blockName}:${targetFlowName}`
          } else {
            // Search other blocks
            for (const [otherBlock, otherBlockDef] of Object.entries(blocks)) {
              if (otherBlock === blockName) continue
              const otherFlows = otherBlockDef.flow ?? {}
              if (otherFlows[targetFlowName]) {
                targetFlowNodeId = `flow:${otherBlock}:${targetFlowName}`
                break
              }
            }
          }

          if (targetFlowNodeId) {
            edges.push({
              id: nextEdgeId(),
              source: flowNodeId,
              target: targetFlowNodeId,
              type: 'callFlow',
              label: targetFlowName,
            })
          }
        }
      })
    }
  }

  // -------------------------------------------------------------------------
  // 2. Build state nodes from crossRef.stateUsageIndex
  // -------------------------------------------------------------------------

  for (const [flowName, usageEntries] of crossRef.stateUsageIndex) {
    // Find the block that owns this flow
    let ownerBlock = 'global'
    for (const [blockName, block] of Object.entries(blocks)) {
      const blockFlows = block.flow ?? {}
      if (blockFlows[flowName]) {
        ownerBlock = blockName
        break
      }
    }

    const flowNodeId = `flow:${ownerBlock}:${flowName}`

    for (const entry of usageEntries) {
      const isAppData = isAppDataScope(entry.key, entityIndex)
      const sId = stateNodeId(entry.key, ownerBlock, isAppData)

      let stateNode = stateMap.get(sId)
      if (!stateNode) {
        stateNode = {
          id: sId,
          type: 'state',
          key: entry.key,
          blockName: isAppData ? 'appData' : ownerBlock,
          scope: isAppData ? 'appData' : 'local',
          readByFlows: [],
          writtenByFlows: [],
        }
        stateMap.set(sId, stateNode)
      }

      if (entry.mode === 'write') {
        if (!stateNode.writtenByFlows.includes(flowNodeId)) {
          stateNode.writtenByFlows.push(flowNodeId)
        }
        // flow -> state (write edge)
        edges.push({
          id: nextEdgeId(),
          source: flowNodeId,
          target: sId,
          type: 'write',
          label: entry.key,
        })
      } else {
        if (!stateNode.readByFlows.includes(flowNodeId)) {
          stateNode.readByFlows.push(flowNodeId)
        }
        // state -> flow (read edge)
        edges.push({
          id: nextEdgeId(),
          source: sId,
          target: flowNodeId,
          type: 'read',
          label: entry.key,
        })
      }
    }
  }

  // -------------------------------------------------------------------------
  // 3. Extract UI bindings from element #{var} expressions
  // -------------------------------------------------------------------------

  for (const [blockName, block] of Object.entries(blocks)) {
    const root = block.ui?.root
    if (!root) continue

    walkUiElements(root as UiNode, (el) => {
      // Stringify element props (excluding children and id/type) for expression scanning
      const { children, id, type, ...props } = el
      const propsStr = JSON.stringify(props)
      const refs = extractUiBindingRefs(propsStr)

      for (const ref of refs) {
        const bindingId = `ui:${blockName}:${id}:${ref}`
        uiBindings.push({
          id: bindingId,
          type: 'uiBinding',
          blockName,
          elementId: String(id),
          boundVariable: ref,
        })
      }
    })
  }

  // -------------------------------------------------------------------------
  // 4. Create state -> uiBinding (bind) edges
  // -------------------------------------------------------------------------

  for (const binding of uiBindings) {
    // Find matching state node — try multiple key patterns
    let matchedStateId: string | null = null

    // Direct match by variable name
    for (const [sId, stateNode] of stateMap) {
      if (stateNode.key === binding.boundVariable) {
        matchedStateId = sId
        break
      }
      // Also match appData.X for binding to X or appData.X
      if (stateNode.key === `appData.${binding.boundVariable}`) {
        matchedStateId = sId
        break
      }
      // Match appData.X binding to key "appData.X"
      if (binding.boundVariable.startsWith('appData.') && stateNode.key === binding.boundVariable) {
        matchedStateId = sId
        break
      }
    }

    if (matchedStateId) {
      edges.push({
        id: nextEdgeId(),
        source: matchedStateId,
        target: binding.id,
        type: 'bind',
        label: binding.boundVariable,
      })
    }
  }

  // -------------------------------------------------------------------------
  // 5. Navigate edges from crossRef.navigationIndex
  // -------------------------------------------------------------------------

  for (const [flowName, navEntries] of crossRef.navigationIndex) {
    // Find flow node ID
    let flowNodeId: string | null = null
    for (const flow of flows) {
      if (flow.flowName === flowName) {
        flowNodeId = flow.id
        break
      }
    }

    if (flowNodeId) {
      for (const navEntry of navEntries) {
        edges.push({
          id: nextEdgeId(),
          source: flowNodeId,
          target: `page:${navEntry.targetPageId ?? navEntry.targetRoute}`,
          type: 'navigate',
          label: navEntry.targetRoute,
        })
      }
    }
  }

  return {
    datasources,
    flows,
    states: Array.from(stateMap.values()),
    uiBindings,
    edges,
  }
}
